import React from 'react'
import {
  CheckCircle, CircleNotch, WarningCircle, SkipForward,
} from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { nodeIcon } from '../../nodeCatalog'
import type { PlaygroundStep } from './usePlaygroundWalker'
import type { NodeType, WorkflowNode } from '@/types/workflow'

/**
 * The node's identity glyph. Was a private 21-entry map that had drifted from
 * the canvas on three types (`action`, `enrichContacts`, `linkedInAction`);
 * `nodeCatalog` owns it now and supplies its own fallback for a stale type.
 */
const NodeGlyph: React.FC<{ type: NodeType }> = ({ type }) => {
  const Icon = nodeIcon(type)
  return <Icon className="w-4 h-4" />
}

const statusStyles: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground border-border/60 dark:bg-white/[0.04] dark:border-white/[0.08]',
  active: 'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/40 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40',
  generating: 'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/40 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/40',
  completed: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/30',
  skipped: 'bg-muted/50 text-muted-foreground/50 border-border/50 dark:bg-white/[0.02] dark:border-white/[0.06]',
  // Not a member of the status union — keyed off `step.error` instead, so a
  // failed step keeps its real status ('active' for a first attempt,
  // 'completed' when a re-run failed over still-valid content) and stays
  // navigable, while the marker stops claiming success.
  failed: 'bg-red-500/10 text-red-600 border-red-500/30 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/30',
}

interface Props {
  steps: PlaygroundStep[]
  currentStepIndex: number
  /** The step currently shown in the detail panel (may differ from the frontier). */
  viewingStepIndex?: number
  /** Nodes not reached yet; shown muted below the walked steps so every step is visible. */
  upcomingNodes?: WorkflowNode[]
  onStepClick?: (index: number) => void
}

export const PlaygroundTimeline: React.FC<Props> = ({ steps, currentStepIndex, viewingStepIndex, upcomingNodes = [], onStepClick }) => {
  return (
    <div className="p-2">
      {steps.map((step, i) => {
        const isViewing = i === (viewingStepIndex ?? currentStepIndex)
        const isFirst = i === 0
        const isLast = i === steps.length - 1 && upcomingNodes.length === 0
        // Distinct from status === 'skipped' (a branch never taken): this step
        // ran, but the user chose "Skip & Continue" instead of generating --
        // it's `completed` with no content to show.
        const generationSkipped = step.status === 'completed'
          && (step.nodeType === 'sendEmail' || step.nodeType === 'generateDeck')
          && !step.generatedEmail && !step.generatedDeck
        // Completed and active steps can be revisited; pending/skipped cannot.
        const markerStatus = (step.status === 'active' || step.status === 'generating') && !isViewing ? 'pending' : step.status
        const clickable = !!onStepClick && (step.status === 'completed' || step.status === 'active')
        // Connector colour reflects the source step: done = brand-tinted, skipped = faint.
        const connector =
          step.status === 'skipped' ? 'bg-border/40 dark:bg-white/[0.06]'
          : step.status === 'completed' ? 'bg-emerald-500/40 dark:bg-emerald-500/30'
          : 'bg-border dark:bg-white/10'
        return (
          <div
            key={`${step.nodeId}-${i}`}
            onClick={clickable ? () => onStepClick!(i) : undefined}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStepClick!(i) } } : undefined}
            className={cn(
              'group relative flex min-h-[56px] items-center gap-3 rounded-lg px-2 transition-colors',
              isViewing && 'bg-[#5e6ad2]/10 ring-1 ring-[#5e6ad2]/30 dark:bg-[#5e6ad2]/[0.14]',
              clickable && 'cursor-pointer',
              clickable && !isViewing && 'hover:bg-accent/40 dark:hover:bg-white/[0.04]',
              clickable && 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5e6ad2]/40',
            )}
          >
            {/* Rail: connector segments above and below the vertically-centred
                node. Each stops ~5px short of the circle (16px radius + 5px gap =
                21px from centre) so the line never crosses the translucent node.
                Adjacent rows meet at the row edge → one continuous timeline with
                clean gaps at every node, at any row height. */}
            <div className="relative flex w-8 flex-shrink-0 items-center justify-center self-stretch">
              {!isFirst && <span className={cn('absolute left-1/2 top-0 w-px -translate-x-1/2', connector)} style={{ height: 'calc(50% - 21px)' }} aria-hidden="true" />}
              {!isLast && <span className={cn('absolute bottom-0 left-1/2 w-px -translate-x-1/2', connector)} style={{ top: 'calc(50% + 21px)' }} aria-hidden="true" />}
              <div className={cn(
                'relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border transition-colors',
                statusStyles[step.error ? 'failed' : markerStatus],
              )}>
                {step.error ? (
                  <WarningCircle className="w-4 h-4" />
                ) : step.status === 'generating' ? (
                  <CircleNotch className="w-4 h-4 animate-spin" />
                ) : step.status === 'completed' ? (
                  <CheckCircle className="w-4 h-4" />
                ) : step.status === 'skipped' ? (
                  <SkipForward className="w-3.5 h-3.5" />
                ) : (
                  <NodeGlyph type={step.nodeType} />
                )}
              </div>
            </div>

            {/* Label */}
            <div className={cn('min-w-0 flex-1 py-2', step.status === 'skipped' && 'opacity-50')}>
              <span className={cn(
                'block truncate text-sm',
                isViewing ? 'font-emphasis text-foreground'
                  : step.status === 'pending' ? 'text-muted-foreground'
                  : 'text-foreground/80',
              )}>
                {step.nodeData.label || step.nodeType}
              </span>
              {step.error && (
                <span role="alert" className="block text-xs text-red-600 dark:text-red-400 break-words">
                  {step.error}
                </span>
              )}
              {step.status === 'skipped' && (
                <span className="text-xs text-muted-foreground/60">Skipped</span>
              )}
              {generationSkipped && (
                <span className="text-xs text-muted-foreground/60">Generation skipped</span>
              )}
              {step.branchChoice && (
                <span className="block text-xs text-[#5e6ad2] dark:text-[#a5b4fc]">{step.branchChoice}</span>
              )}
              {step.generatedEmail && (
                <span className="block truncate text-xs text-muted-foreground">
                  {step.generatedEmail.subject}
                </span>
              )}
              {step.usageLog && step.status === 'completed' && (
                <span className="block text-[10px] font-mono text-muted-foreground tabular-nums">
                  {step.usageLog.reduce((s, e) => s + e.input_tokens + e.output_tokens, 0).toLocaleString()} tok | ${step.usageLog.reduce((s, e) => s + e.cost, 0).toFixed(4)}
                </span>
              )}
              {step.replyScenario && step.status === 'completed' && (
                <span className={cn(
                  'block text-xs',
                  step.replyScenario === 'positive' ? 'text-emerald-600 dark:text-emerald-400'
                    : step.replyScenario === 'not_interested' || step.replyScenario === 'negative' ? 'text-red-500 dark:text-red-400'
                    : 'text-muted-foreground',
                )}>
                  Reply: {step.replyScenario.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>
        )
      })}
      {upcomingNodes.map((node, j) => {
        const isLast = j === upcomingNodes.length - 1
        return (
          <div
            key={`upcoming-${node.id}`}
            data-testid="upcoming-step"
            className="relative flex min-h-[56px] items-center gap-3 rounded-lg px-2"
          >
            <div className="relative flex w-8 flex-shrink-0 items-center justify-center self-stretch">
              <span className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-border dark:bg-white/10" style={{ height: 'calc(50% - 21px)' }} aria-hidden="true" />
              {!isLast && <span className="absolute bottom-0 left-1/2 w-px -translate-x-1/2 bg-border dark:bg-white/10" style={{ top: 'calc(50% + 21px)' }} aria-hidden="true" />}
              <div className={cn('relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border', statusStyles.pending)}>
                <NodeGlyph type={node.type} />
              </div>
            </div>
            <div className="min-w-0 flex-1 py-2">
              <span className="block truncate text-sm text-muted-foreground">{node.data.label || node.type}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}
